import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coachApi, DashboardStats, TeamMemberWithUser, PlayerProfileResponse } from '@/lib/coach-api';
import { playerTeamApi, TeamWithCoach, PlayerCreditsResponse } from '@/lib/player-team-api';
import type { Team, TrainingPlan, ExerciseVideoFeedback, CoachNote } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';

// ============================================================
// Coach Hooks
// ============================================================

/**
 * Hook to fetch dashboard stats for the coach
 */
export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: ['coach', 'dashboard', 'stats'],
    queryFn: () => coachApi.getDashboardStats(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch the coach's team
 */
export function useCoachTeam() {
  return useQuery<Team[]>({
    queryKey: ['coach', 'teams'],
    queryFn: () => coachApi.getTeams(),
  });
}

/**
 * Hook to fetch team members
 */
export function useTeamMembers(teamId: number | undefined) {
  return useQuery<TeamMemberWithUser[]>({
    queryKey: ['coach', 'teams', teamId, 'members'],
    queryFn: () => coachApi.getTeamMembers(teamId!),
    enabled: !!teamId,
  });
}

/**
 * Hook to fetch a player's profile
 */
export function usePlayerProfile(playerId: number | undefined) {
  return useQuery<PlayerProfileResponse>({
    queryKey: ['coach', 'players', playerId, 'profile'],
    queryFn: () => coachApi.getPlayerProfile(playerId!),
    enabled: !!playerId,
  });
}

/**
 * Hook to fetch a player's training plans
 */
export function usePlayerPlans(playerId: number | undefined) {
  return useQuery<TrainingPlan[]>({
    queryKey: ['coach', 'players', playerId, 'plans'],
    queryFn: () => coachApi.getPlayerPlans(playerId!),
    enabled: !!playerId,
  });
}

/**
 * Hook to fetch a player's videos
 */
export function usePlayerVideos(playerId: number | undefined) {
  return useQuery<ExerciseVideoFeedback[]>({
    queryKey: ['coach', 'players', playerId, 'videos'],
    queryFn: () => coachApi.getPlayerVideos(playerId!),
    enabled: !!playerId,
  });
}

/**
 * Hook to fetch a player's coach notes
 */
export function usePlayerNotes(playerId: number | undefined) {
  return useQuery<CoachNote[]>({
    queryKey: ['coach', 'players', playerId, 'notes'],
    queryFn: () => coachApi.getPlayerNotes(playerId!),
    enabled: !!playerId,
  });
}

/**
 * Hook to create a new team
 */
export function useCreateTeam() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (name: string) => coachApi.createTeam(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['coach', 'dashboard'] });
      toast({
        title: 'Team created',
        description: 'Your team has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create team',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update team settings
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ teamId, updates }: { teamId: number; updates: { name?: string; maxMembers?: number } }) =>
      coachApi.updateTeam(teamId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['coach', 'dashboard'] });
      toast({
        title: 'Team updated',
        description: 'Team settings have been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update team',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to regenerate invite code
 */
export function useRegenerateInviteCode() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (teamId: number) => coachApi.regenerateInviteCode(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['coach', 'dashboard'] });
      toast({
        title: 'Invite code regenerated',
        description: 'A new invite code has been generated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to regenerate invite code',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to remove a team member
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ teamId, memberId }: { teamId: number; memberId: number }) =>
      coachApi.removeMember(teamId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['coach', 'dashboard'] });
      toast({
        title: 'Member removed',
        description: 'The player has been removed from the team.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove member',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to create a coach note
 */
export function useCreateNote() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: {
      athleteUserId: number;
      targetType: 'video' | 'plan';
      targetId: number;
      content: string;
    }) => coachApi.createNote(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'players', variables.athleteUserId, 'notes'] });
      toast({
        title: 'Note added',
        description: 'Your note has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add note',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update a coach note
 */
export function useUpdateNote() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ noteId, content }: { noteId: number; content: string }) =>
      coachApi.updateNote(noteId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'players'] });
      toast({
        title: 'Note updated',
        description: 'Your note has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update note',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to delete a coach note
 */
export function useDeleteNote() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (noteId: number) => coachApi.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', 'players'] });
      toast({
        title: 'Note deleted',
        description: 'Your note has been deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete note',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================
// Player Team Hooks
// ============================================================

/**
 * Hook to fetch the player's current team
 */
export function usePlayerTeam() {
  return useQuery<TeamWithCoach>({
    queryKey: ['player', 'team'],
    queryFn: () => playerTeamApi.getCurrentTeam(),
  });
}

/**
 * Hook to fetch player's credit information
 */
export function usePlayerCredits() {
  return useQuery<PlayerCreditsResponse>({
    queryKey: ['player', 'credits'],
    queryFn: () => playerTeamApi.getCredits(),
  });
}

/**
 * Hook to fetch coach notes for the player
 */
export function useMyCoachNotes() {
  return useQuery<CoachNote[]>({
    queryKey: ['player', 'coach-notes'],
    queryFn: () => playerTeamApi.getMyCoachNotes(),
  });
}

/**
 * Hook to join a team
 */
export function useJoinTeam() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (inviteCode: string) => playerTeamApi.joinTeam(inviteCode),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['player', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['player', 'credits'] });
      toast({
        title: 'Joined team',
        description: `You have joined ${data.team.name}!`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to join team',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to leave the current team
 */
export function useLeaveTeam() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => playerTeamApi.leaveTeam(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['player', 'team'] });
      queryClient.invalidateQueries({ queryKey: ['player', 'credits'] });
      toast({
        title: 'Left team',
        description: 'You have left the team.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to leave team',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
